import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";

interface VideoUrlDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (videoUrl: string) => void;
  isSubmitting?: boolean;
}

export const VideoUrlDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}: VideoUrlDialogProps) => {
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState("");

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) {
      setError("Please enter a video URL");
      return false;
    }

    // Basic URL validation
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      // Check for supported platforms
      const supportedPlatforms = [
        "youtube.com",
        "youtu.be",
        "tiktok.com",
        "vm.tiktok.com",
        "instagram.com",
        "www.youtube.com",
        "www.tiktok.com",
        "www.instagram.com",
      ];

      const isSupported = supportedPlatforms.some(
        (platform) =>
          hostname === platform || hostname.endsWith(`.${platform}`),
      );

      if (!isSupported) {
        setError(
          "Please enter a valid YouTube, TikTok, or Instagram video URL",
        );
        return false;
      }

      setError("");
      return true;
    } catch {
      setError("Please enter a valid URL");
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateUrl(videoUrl)) {
      onSubmit(videoUrl.trim());
      setVideoUrl("");
      setError("");
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSubmitting) {
      onOpenChange(newOpen);
      if (!newOpen) {
        setVideoUrl("");
        setError("");
      }
    }
  };

  const getPlatformFromUrl = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      if (hostname.includes("youtube") || hostname.includes("youtu.be")) {
        // Check if it's a YouTube Short
        if (urlObj.pathname.includes("/shorts/")) {
          return "YouTube Shorts";
        }
        return "YouTube";
      }
      if (hostname.includes("tiktok")) return "TikTok";
      if (hostname.includes("instagram")) return "Instagram";
      return null;
    } catch {
      return null;
    }
  };

  const detectedPlatform = videoUrl ? getPlatformFromUrl(videoUrl) : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Video URL</DialogTitle>
          <DialogDescription>
            Enter the URL of the posted video to track its analytics and mark
            this hook as filmed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="videoUrl">Video URL</Label>
            <Input
              id="videoUrl"
              placeholder="https://youtube.com/watch?v=... or https://tiktok.com/@user/video/..."
              value={videoUrl}
              onChange={(e) => {
                setVideoUrl(e.target.value);
                if (error) setError(""); // Clear error when user starts typing
              }}
              className={error ? "border-red-500" : ""}
              disabled={isSubmitting}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            {detectedPlatform && !error && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                {detectedPlatform} video detected
              </p>
            )}
          </div>

          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Supported platforms:</strong>
            </p>
            <ul className="text-sm text-muted-foreground mt-1 space-y-1">
              <li>• YouTube (including Shorts)</li>
              <li>• TikTok</li>
              <li>• Instagram (Reels and Posts)</li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !videoUrl.trim()}>
              {isSubmitting ? "Adding..." : "Add Video"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
